/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import * as React from "react";
import { Helmet } from "react-helmet-async";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { Box, CircularProgress } from "@mui/joy";
import { ThemeProvider } from "styles/theme/ThemeProvider";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "types";
import { useAuth } from "aws/AuthContext";
import { pdfjs } from "react-pdf";

import { NotFoundPage } from "./components/NotFoundPage/Loadable";
import { PRIVACY_POLICY_ROUTE, TNC_ROUTE, ROOT_ROUTE, AUTH_ROUTE, SIGN_UP_ROUTE, RESET_PASSWORD_ROUTE, DASHBOARD_ROUTE, EXAM_ROUTE, STUDENT_EXAM_ROUTE, DOCUMENTATION_ROUTE, NOT_FOUND_ROUTE } from "./constants/route";
import { TnCPage } from "./pages/TnCPage";
import { StudentAssignmentSubmissionPage } from "./pages/StudentAssignmentSubmissionPage";
import { FadingLogo } from "./components/FadingLogo";
import { DashboardPage } from "./pages/DashboardPage";
import OnBoardingPage from "./pages/OnBoardingPage/OnBoardingPage";
import DocumentationPage from "./pages/DocumentationPage/DocumentationPage";

import "styles/global.css";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicyPage";
import { ExamPage } from "./pages/ExamPage/ExamPage";
import CheckoutPage from "./pages/CheckoutPage/CheckoutPage";


export function App() {
  const { i18n } = useTranslation();
  const { isAuthenticated, isLoading, user } = useAuth();

  const uid = user?.userId;
  const isAuthLoaded = !isLoading;

  // Use AWS auth isAuthenticated value
  const isUserAuthenticated = React.useMemo(
    () => isAuthenticated && uid,
    [isAuthenticated, uid]
  );

  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;


  return (
    <ThemeProvider>
      <BrowserRouter>
        <Helmet
          titleTemplate="%s - AcademIX Student"
          defaultTitle="AcademIX Student"
          htmlAttributes={{ lang: i18n.language }}
        >
          <meta name="description" content="AcademIX Student Portal" />
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" />
          <link
            href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap"
            rel="stylesheet"
          ></link>
          <link
            rel="stylesheet"
            href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;900&display=swap"
          />
        </Helmet>
        {isAuthLoaded ? (
          <Routes>
            <Route
              path={ROOT_ROUTE}
              element={
                isUserAuthenticated ? (
                  <Navigate to={DASHBOARD_ROUTE} replace />
                ) : (
                  <Navigate to={AUTH_ROUTE} replace />
                )
              }
            />
            <Route
              path={AUTH_ROUTE}
              element={
                isUserAuthenticated ? (
                  <Navigate to={DASHBOARD_ROUTE} replace />
                ) : (
                  <OnBoardingPage type="sign-in" />
                )
              }
            />
            <Route
              path={SIGN_UP_ROUTE}
              element={
                isUserAuthenticated ? (
                  <Navigate to={DASHBOARD_ROUTE} replace />
                ) : (
                  <OnBoardingPage type="sign-up" />
                )
              }
            />
            <Route
              path={RESET_PASSWORD_ROUTE}
              element={
                isUserAuthenticated ? (
                  <Navigate to={DASHBOARD_ROUTE} replace />
                ) : (
                  <OnBoardingPage type="forgot-password" />
                )
              }
            />
            <Route
              path="/exam/checkout/:examId"
              element={<CheckoutPage />}
            />
            <Route path={DASHBOARD_ROUTE} element={<DashboardPage />} />
            <Route path={EXAM_ROUTE} element={<ExamPage />} />
            <Route
              path={STUDENT_EXAM_ROUTE}
              element={<StudentAssignmentSubmissionPage />}
            />
            <Route path={DOCUMENTATION_ROUTE} element={<DocumentationPage />} />
            <Route path={TNC_ROUTE} element={<TnCPage />} />
            <Route
              path={PRIVACY_POLICY_ROUTE}
              element={<PrivacyPolicyPage />}
            />
            <Route path={NOT_FOUND_ROUTE} element={<NotFoundPage />} />
          </Routes>
        ) : (
          <Box
            sx={{
              width: "100vw",
              height: " 100vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <FadingLogo />
          </Box>
        )}
      </BrowserRouter>
    </ThemeProvider>
  );
}
